import { useMemo } from "react";
import { MetricCard } from "@/components/cards/MetricCard";
import { EngagementChart } from "@/components/charts/EngagementChart";
import { BarChartCard } from "@/components/charts/BarChartCard";
import { PostsTable } from "@/components/tables/PostsTable";
import { TopFBPosts, TopIGPosts } from "@/components/cards/TopPostCard";
import { EngagementDistribution, WorstPosts, RestaurantLeaderboard, ViralPosts, FollowerTrend } from "@/components/charts/PerformanceAnalytics";
import { FilterBar } from "@/components/layout/FilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { useFilteredFB, useFilteredIG } from "@/hooks/useFilteredData";
import { fillDateRange } from "@/lib/chart-utils";
import { getPostTypeLabel } from "@/lib/post-type-labels";
import { getPreviousPeriod } from "@/lib/period-compare";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, Heart, MessageCircle, Hash, TrendingUp, BarChart3 } from "lucide-react";
import { LoadingState } from '@/components/ui/spinner';

export default function SocialOverviewPage() {
  const fbHook = useFilteredFB();
  const igHook = useFilteredIG();

  const setFilters = (f: typeof fbHook.filters) => {
    fbHook.setFilters(f);
    igHook.setFilters(f);
  };

  const fbPosts = fbHook.data.filter((p) => p.post_id);
  const igPosts = igHook.data.filter((p) => p.media_id);

  // Current period metrics
  const totalPosts = fbPosts.length + igPosts.length;
  const totalReach = fbPosts.reduce((s, p) => s + p.post_impressions_unique, 0) + igPosts.reduce((s, p) => s + p.media_reach, 0);
  const totalEngagement = fbPosts.reduce((s, p) => s + p.page_post_engagements, 0) + igPosts.reduce((s, p) => s + p.media_engagement, 0);
  const totalComments = fbPosts.reduce((s, p) => s + p.post_comments_total, 0) + igPosts.reduce((s, p) => s + p.media_comments_count, 0);
  const engagementRate = totalReach > 0 ? totalEngagement / totalReach : 0;
  const avgReachPerPost = totalPosts > 0 ? Math.round(totalReach / totalPosts) : 0;
  const avgEngPerPost = totalPosts > 0 ? Math.round(totalEngagement / totalPosts) : 0;
  const avgCommentsPerPost = totalPosts > 0 ? totalComments / totalPosts : 0;

  // Previous period for comparison
  const prev = useMemo(() => {
    const { dateFrom, dateTo } = getPreviousPeriod(fbHook.filters.dateFrom, fbHook.filters.dateTo);
    const prevFB = fbHook.allData.filter((p) => p.post_id && p.date >= dateFrom && p.date <= dateTo);
    const prevIG = igHook.allData.filter((p) => p.media_id && p.date >= dateFrom && p.date <= dateTo);
    const posts = prevFB.length + prevIG.length;
    const reach = prevFB.reduce((s, p) => s + p.post_impressions_unique, 0) + prevIG.reduce((s, p) => s + p.media_reach, 0);
    const eng = prevFB.reduce((s, p) => s + p.page_post_engagements, 0) + prevIG.reduce((s, p) => s + p.media_engagement, 0);
    const comments = prevFB.reduce((s, p) => s + p.post_comments_total, 0) + prevIG.reduce((s, p) => s + p.media_comments_count, 0);
    return {
      posts, reach, eng, comments,
      engRate: reach > 0 ? eng / reach : 0,
      avgReach: posts > 0 ? Math.round(reach / posts) : 0,
      avgEng: posts > 0 ? Math.round(eng / posts) : 0,
      avgComments: posts > 0 ? comments / posts : 0,
    };
  }, [fbHook.allData, igHook.allData, fbHook.filters.dateFrom, fbHook.filters.dateTo]);

  const engagementTrend = useMemo(() => {
    const map = new Map<string, number>();
    fbPosts.forEach((p) => map.set(p.date, (map.get(p.date) || 0) + p.page_post_engagements));
    igPosts.forEach((p) => map.set(p.date, (map.get(p.date) || 0) + p.media_engagement));
    const raw = Array.from(map.entries()).map(([date, value]) => ({ date, value }));
    return fillDateRange(raw, fbHook.filters.dateFrom, fbHook.filters.dateTo);
  }, [fbPosts, igPosts, fbHook.filters.dateFrom, fbHook.filters.dateTo]);

  const postTypeData = useMemo(() => {
    const map = new Map<string, { count: number; engagement: number }>();
    fbPosts.forEach((p) => {
      const t = p.type || "unknown";
      const curr = map.get(t) || { count: 0, engagement: 0 };
      curr.count++; curr.engagement += p.page_post_engagements;
      map.set(t, curr);
    });
    igPosts.forEach((p) => {
      const t = p.media_product_type || p.media_type || "unknown";
      const curr = map.get(t) || { count: 0, engagement: 0 };
      curr.count++; curr.engagement += p.media_engagement;
      map.set(t, curr);
    });
    return Array.from(map.entries())
      .map(([name, { count, engagement }]) => ({ name: getPostTypeLabel(name), value: count, value2: count > 0 ? Math.round(engagement / count) : 0 }))
      .sort((a, b) => b.value - a.value);
  }, [fbPosts, igPosts]);

  if (fbHook.loading || igHook.loading) return <LoadingState label="Načítám data…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Sociální sítě" description="Přehled výkonu příspěvků" />

      <FilterBar filters={fbHook.filters} onChange={setFilters} showPlatform showPostType accounts={[...new Set([...fbHook.accounts, ...igHook.accounts])]} postTypes={[...new Set([...fbHook.postTypes, ...igHook.postTypes])]} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Příspěvků" value={totalPosts} previousValue={prev.posts} icon={<Hash className="h-4 w-4 text-primary" />} subtitle={`FB: ${fbPosts.length} | IG: ${igPosts.length}`} />
        <MetricCard label="Dosah" value={totalReach} previousValue={prev.reach} icon={<Eye className="h-4 w-4 text-primary" />} />
        <MetricCard label="Engagement" value={totalEngagement} previousValue={prev.eng} icon={<Heart className="h-4 w-4 text-primary" />} />
        <MetricCard label="Eng. rate" value={engagementRate} previousValue={prev.engRate} format="percent" icon={<TrendingUp className="h-4 w-4 text-primary" />} />
        <MetricCard label="Ø dosah" value={avgReachPerPost} previousValue={prev.avgReach} icon={<Eye className="h-4 w-4 text-primary" />} subtitle="na post" />
        <MetricCard label="Ø eng." value={avgEngPerPost} previousValue={prev.avgEng} icon={<BarChart3 className="h-4 w-4 text-primary" />} subtitle="na post" />
        <MetricCard label="Ø komentáře" value={avgCommentsPerPost} previousValue={prev.avgComments} format="decimal" icon={<MessageCircle className="h-4 w-4 text-primary" />} subtitle="na post" />
        <MetricCard label="Komentáře" value={totalComments} previousValue={prev.comments} icon={<MessageCircle className="h-4 w-4 text-primary" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementChart title="Engagement trend" data={engagementTrend} color="#c9a84c" label1="Engagement" fbPosts={fbPosts} igPosts={igPosts} />
        <BarChartCard title="Výkon dle typu" data={postTypeData} color="#6b8fd4" color2="#e8c36a" label1="Počet" label2="Prům. engagement" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopFBPosts posts={fbPosts} title="Top FB příspěvky (engagement)" />
        <TopIGPosts posts={igPosts} title="Top IG příspěvky (engagement)" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ViralPosts fbPosts={fbPosts} igPosts={igPosts} />
        <WorstPosts fbPosts={fbPosts} igPosts={igPosts} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementDistribution fbPosts={fbPosts} igPosts={igPosts} />
        <FollowerTrend fbPosts={fbHook.data} igPosts={igHook.data} />
      </div>

      <RestaurantLeaderboard fbPosts={fbPosts} igPosts={igPosts} />

      <Tabs defaultValue="facebook">
        <TabsList>
          <TabsTrigger value="facebook">Facebook ({fbPosts.length})</TabsTrigger>
          <TabsTrigger value="instagram">Instagram ({igPosts.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="facebook" className="mt-4">
          <PostsTable posts={fbPosts} platform="facebook" />
        </TabsContent>
        <TabsContent value="instagram" className="mt-4">
          <PostsTable posts={igPosts} platform="instagram" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
